/**
 * Reed.ai Design System — Logo
 *
 * Fixed Reed.ai wordmark lockups sourced from the 2026 standalone brand
 * guidelines. The lockup must not be reconstructed with live text, recoloured,
 * cropped, rotated, or have the .ai / dots separated from the mark.
 *
 * Usage rules from the guidelines:
 * - dark theme = Blue & Pink, for light backgrounds
 * - light theme = White & Pink, for dark backgrounds
 * - showBeta uses the approved beta lockup asset
 * - minimum digital wordmark width is 96px; below that, use the favicon/orb system
 */

import logoDark from '../../assets/brand/logo-dark.svg';
import logoDarkBeta from '../../assets/brand/logo-dark-beta.svg';
import logoLight from '../../assets/brand/logo-light.svg';
import logoLightBeta from '../../assets/brand/logo-light-beta.svg';
import { cn } from '../../utils/cn';

export type LogoTheme = 'light' | 'dark';
export type LogoSize = 'nav' | 'sm' | 'md' | 'lg';

export interface LogoProps {
  /** light = white/pink asset for dark backgrounds; dark = blue/pink asset for light backgrounds */
  theme?: LogoTheme;
  size?: LogoSize;
  /**
   * Show the official Beta lockup.
   * @deprecated The Beta badge is being removed everywhere (FED design
   * priority doc, 31.07 — see issue #546). Do not use on new surfaces.
   */
  showBeta?: boolean;
  /** Accessible label for the image; pass an empty string only for decorative usage. */
  ariaLabel?: string;
  className?: string;
}

const SIZE_MAP: Record<LogoSize, string> = {
  // 80px — the navigation wordmark per Figma O / Navigation (node 8500:2660).
  // Sits below the 96px general minimum; sized by the nav spec's authority.
  nav: 'w-20 h-auto',
  sm: 'w-26 h-auto', // closest P0 token to the 96px minimum digital wordmark width
  md: 'h-auto w-auto',
  lg: 'w-40 h-auto',
};

const LOGO_ASSETS: Record<LogoTheme, { standard: string; beta: string }> = {
  dark: {
    standard: logoDark,
    // The beta SVG filenames are background-oriented: Logo-Light-Beta is the blue/pink mark for light backgrounds.
    beta: logoLightBeta,
  },
  light: {
    standard: logoLight,
    // Logo-Dark-Beta is the white/pink mark for dark backgrounds.
    beta: logoDarkBeta,
  },
};

/**
 * Reed.ai Logo
 *
 * @example
 * // White & Pink, for Piccadilly Blue / dark backgrounds
 * <Logo theme="light" />
 *
 * // Blue & Pink, for light backgrounds
 * <Logo theme="dark" />
 *
 * // Official beta lockup
 * <Logo theme="dark" showBeta />
 */
export function Logo({
  theme = 'dark',
  size = 'md',
  showBeta = false,
  ariaLabel,
  className,
}: LogoProps) {
  const src = showBeta ? LOGO_ASSETS[theme].beta : LOGO_ASSETS[theme].standard;
  const label = ariaLabel ?? (showBeta ? 'Reed.ai Beta' : 'Reed.ai');

  return (
    <img
      src={src}
      alt={label}
      draggable={false}
      className={cn(
        'inline-block w-auto shrink-0 object-contain select-none',
        SIZE_MAP[size],
        className,
      )}
    />
  );
}

export default Logo;
